import { DOW_LABELS, addDays, parseDay } from "./date";
import { noSkippedMeals, older, pregnancy, type DeviceAnswers } from "./eating";
import { PATTERN_WORDS, daySplit, isDisliked, windowTimes, WINDOW_STARTS } from "./eatingQuestions";
import { familyByKey, inFamily, knownSafe, nameHasWord, type EatingPattern } from "./foodTags";
import { reportion, shortFoodName } from "./foodView";
import { MEALS, deficitTarget, entriesFor, entryFor, fitFactor, planFor, totalsOf, type IntakeTotals } from "./intake";
import { dailyTargets } from "./nutrition";
import type { Allergen, FoodEntry, FoodGroup, FoodItem, KayaState, MealSlot, PlanItem } from "./types";

/**
 * The planner: a week of food built from what the person already eats, and every choice said in
 * words. (ADR-043; plan §3.5; research questionnaire-and-planning.md §4.2)
 *
 * Deterministic and pure: the same state, table and seed give the same plan, on any phone. A day
 * is a skeleton of meal slots, each with a share of the budget and a list of roles; each role is
 * filled with the best-scored allowed food (how often the person logs it, less a dislike, less
 * whatever the last two days already had, plus protein while the meal is short); the meal is
 * scaled to its share and rounded to the household unit; the day is checked and a bounded
 * like-for-like swap loop repairs what it can. Whatever is still short is said with a way to close
 * it, never forced. No solver: it would find absurd corners and could not say why.
 *
 * Generated items take derived ids `g:<day>:<slot>:<n>` so two offline phones plan a day once, and
 * only empty slots are filled — nothing the person placed or logged is ever replaced. The writers
 * are in `store/toggles.ts` (`writeGenerated`, `regenerateMeal`).
 */

/* ---------- targets ---------- */

/** What a planned day is judged against. `kcal` is the day's budget; on a fast day it is Out, the
 *  deficit left out (`deficit` is then 0). */
export interface PlanTargets {
  kcal: number;
  protein: number;
  /** grams a meal should reach — see `planMealFloor` */
  mealFloor: number;
  sodiumMax: number;
  satFatMax: number | null;
  fibre: number | null;
  iron: number | null;
  calcium: number;
  potassium: number;
  /** the deficit this budget already holds, for "the day's deficit drops from ~500 to ~316" */
  deficit: number;
}

/**
 * The per-meal protein a plan aims every meal at: 25 g, the research's figure (PROT-AGE's
 * 25–30 g a meal), for everyone. `eating.ts`'s 20 g adult floor is where a *logged* meal is called
 * short; a plan is built to the better number, and the boards judge every meal against 25.
 */
export const PLAN_MEAL_PROTEIN_G = 25;

export function planMealFloor(mealG: number): number {
  return Math.max(PLAN_MEAL_PROTEIN_G, mealG);
}

/**
 * The targets a planned day is built to: `nutrition.ts::dailyTargets` for that date with no Work
 * yet (a session only adds to a day when it happens), the plan's meal floor, and on a fast day no
 * deficit — the fast is the day's gap, and a budget 500 under Out on a vrat day would ask for two.
 * Null without the body numbers.
 */
export function planTargetsFor(S: KayaState, ds: string, fast: boolean, device?: DeviceAnswers): PlanTargets | null {
  const day = parseDay(ds);
  const T = dailyTargets(S, day, 0, device);
  if (T.kcal === null) return null;
  const deficit = deficitTarget(S, day, device);
  return {
    kcal: fast ? T.kcal + deficit : T.kcal,
    protein: T.protein,
    mealFloor: planMealFloor(T.mealProtein),
    sodiumMax: T.sodiumMax,
    satFatMax: T.satFatMax,
    fibre: T.fibre,
    iron: T.iron,
    calcium: T.calcium,
    potassium: T.potassium,
    deficit: fast ? 0 : deficit,
  };
}

/** Why no plan is made, or null. A plan for a pregnancy is a doctor's (DGI 2024 adds energy and
 *  protein and Kaya does not model how much). */
export function planBlocked(S: KayaState, hasBudget: boolean): string | null {
  const p = pregnancy(S);
  if (p) return `A doctor should set these while you are ${p}. Kaya does not plan food for this time.`;
  if (!hasBudget) return "Add your year of birth and sex first — a plan needs a budget to share out.";
  return null;
}

/* ---------- skeletons ---------- */

export type RoleKey = "main" | "protein" | "cereal" | "side" | "curd" | "drink" | "fruit" | "snack" | "seeds" | "nuts" | "veg";

export interface Role {
  key: RoleKey;
  groups: readonly FoodGroup[];
  /** filled only while the meal still has room — a side dish, a fruit */
  optional?: boolean;
}

const role = (key: RoleKey, groups: FoodGroup[], optional = false): Role => ({ key, groups, optional });

const PROTEIN_GROUPS: FoodGroup[] = ["egg", "dairy", "legume", "meat", "fish"];
/** Grams a food must bring, at the portion the person eats, to be a meal's protein. */
const PROTEIN_ROLE_MIN_G = 7;
/** The most the protein bonus adds to a score: a little more than a food logged once more a meal. */
const PROTEIN_BONUS_MAX = 10;
/** An optional role — a fruit, a second side — is filled only while this share of the meal is left. */
const OPTIONAL_ROOM = 0.03;

/** The roles of each meal, shaped on DGI 2024's sample days: a breakfast of a cereal or dal dish,
 *  a protein and a drink; a lunch and dinner of a protein, a cereal and sides. */
const ROLES = {
  breakfast: [
    role("main", ["grain", "legume"]),
    role("protein", PROTEIN_GROUPS),
    role("drink", ["drink", "fruit", "dairy"]),
    role("fruit", ["fruit"], true),
  ],
  lunch: [
    role("protein", PROTEIN_GROUPS),
    role("cereal", ["grain"]),
    role("side", ["veg", "leafy", "legume"]),
    role("curd", ["dairy"]),
  ],
  dinner: [
    role("protein", PROTEIN_GROUPS),
    role("cereal", ["grain"]),
    role("side", ["legume", "dairy", "veg", "leafy"], true),
    role("side", ["legume", "dairy", "veg", "leafy"], true),
  ],
  snack: [role("snack", ["legume", "nut", "fruit", "dairy"]), role("drink", ["drink"], true)],
  /** the two-meal day's morning carries the day: breakfast and lunch in one */
  morning: [
    role("main", ["grain", "legume"]),
    role("protein", PROTEIN_GROUPS),
    role("protein", PROTEIN_GROUPS),
    role("cereal", ["grain"]),
    role("side", ["legume", "veg", "leafy"]),
    role("curd", ["dairy"]),
    role("drink", ["drink"]),
    role("fruit", ["fruit"], true),
  ],
  /** a light evening: seeds, curd, nuts, fruit (the owner's own evening — plan §8 note 1) */
  light: [role("seeds", ["nut"]), role("curd", ["dairy"]), role("nuts", ["nut"]), role("fruit", ["fruit"])],
} satisfies Record<string, Role[]>;

/** The fast day's roles: fruit, milk and curd in the morning; a vrat grain and sides later. */
const FAST_ROLES = {
  breakfast: [role("fruit", ["fruit"]), role("curd", ["dairy"]), role("drink", ["dairy", "drink"])],
  lunch: [role("cereal", ["grain"]), role("side", ["dairy", "veg", "legume"]), role("side", ["veg", "dairy"], true)],
  snack: [role("drink", ["drink"]), role("snack", ["nut", "fruit", "dairy", "grain"])],
  dinner: [
    role("cereal", ["grain"]),
    role("side", ["dairy", "legume"]),
    role("protein", ["dairy", "legume"]),
    role("veg", ["veg", "leafy"], true),
  ],
} satisfies Record<MealSlot, Role[]>;

/** Fast-day shares (the parent's Ekadashi board): 20 / 35 / 10 / 35 — lunch and dinner carry the
 *  vrat grain, and the morning is fruit and milk. */
const FAST_SHARES: Record<MealSlot, number> = { breakfast: 20, lunch: 35, snack: 10, dinner: 35 };

export interface PlanSlot {
  meal: MealSlot;
  label: string;
  /** kcal the slot is planned to */
  share: number;
  roles: readonly Role[];
  /** the two-meal day's light evening */
  light?: boolean;
  /** a weekday canteen lunch: left as a budget, not dishes */
  budgetOnly?: boolean;
  /** tiffin lunch: dishes that travel are preferred */
  tiffin?: boolean;
  /** hour of day, when the window or the person's own log says when */
  time?: number;
  /** the windowed day's first meal, carrying breakfast's share and its protein */
  first?: boolean;
}

export type SkeletonKind = "usual" | "two" | "one" | "window" | "fast";

export interface DaySkeleton {
  kind: SkeletonKind;
  slots: PlanSlot[];
  /** kcal the plan leaves unplanned — a light evening's gap */
  unplanned: number;
  fast: boolean;
}

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

/** A fast the eating answers name for this date: a weekday fast. Ekadashi, Navratri, Ramadan and
 *  "other days" have no calendar here — the person marks the day (package J's fast mark), and the
 *  page passes that in as `marked`. */
export function answersFast(S: KayaState, ds: string): boolean {
  const fasts = S.profile.eating?.fasts ?? [];
  return fasts.includes(WEEKDAY_KEYS[parseDay(ds).getDay()]);
}

function isWorkday(ds: string): boolean {
  const d = parseDay(ds).getDay();
  return d >= 1 && d <= 5;
}

const round10 = (n: number) => Math.round(n / 10) * 10;

/** The meal a split row stands for. */
function mealOfRow(label: string): MealSlot {
  switch (label) {
    case "Breakfast":
    case "Morning":
    case "First meal":
      return "breakfast";
    case "Lunch":
    case "Main meal":
      return "lunch";
    case "Dinner":
      return "dinner";
    default:
      return "snack";
  }
}

/**
 * A day's slots. The shares come from `eatingQuestions.ts::daySplit` — the questionnaire's own
 * split, so the plan and the read-back never disagree — except a fast day, which has its own. At
 * 60+ or on a drug that can bring sugar low, `daySplit` already plans three meals and a snack in
 * place of two meals or one; a fast day keeps all four for them too.
 */
export function daySkeleton(
  S: KayaState,
  ds: string,
  budget: number,
  opts: { fast?: boolean; today?: Date } = {}
): DaySkeleton {
  const a = S.profile.eating ?? {};
  const today = opts.today ?? new Date();
  const isOlder = older(S, today);
  const restricted = noSkippedMeals(S, today);
  const lunchKind = isWorkday(ds) ? a.lunch : "home";
  const times = usualTimes(S);
  const withTime = (s: PlanSlot): PlanSlot => (restricted && times[s.meal] !== undefined ? { ...s, time: times[s.meal] } : s);

  if (opts.fast) {
    const slots = (["breakfast", "lunch", "snack", "dinner"] as MealSlot[]).map((meal) =>
      withTime({ meal, label: LABEL[meal], share: round10((budget * FAST_SHARES[meal]) / 100), roles: FAST_ROLES[meal] })
    );
    return { kind: "fast", slots: fixRounding(slots, budget), unplanned: 0, fast: true };
  }

  const meals = a.meals ?? "3s";
  const split = daySplit(meals, a.evening, { older: isOlder, restricted }, budget);
  const effective = restricted && (meals === "2" || meals === "1") ? "3s" : meals;

  // Several rows of one meal ("Mid-morning" and "Evening" on the 4–5 meal day) are one snack slot.
  const byMeal = new Map<MealSlot, PlanSlot>();
  for (const row of split.rows) {
    const meal = mealOfRow(row.label);
    const cur = byMeal.get(meal);
    if (cur) {
      cur.share += row.kcal;
      continue;
    }
    byMeal.set(meal, { meal, label: LABEL[meal], share: row.kcal, roles: ROLES[meal] });
  }
  let slots = [...byMeal.values()];
  let kind: SkeletonKind = "usual";

  if (effective === "2") {
    kind = "two";
    const light = a.evening !== "full";
    slots = [
      { meal: "breakfast", label: "Morning", share: split.rows[0].kcal, roles: ROLES.morning },
      { meal: "dinner", label: "Evening", share: split.rows[1].kcal, roles: light ? ROLES.light : ROLES.dinner, light },
    ];
  } else if (effective === "1") {
    kind = "one";
    slots = [
      { meal: "lunch", label: "Main meal", share: split.rows[0].kcal, roles: ROLES.morning },
      { meal: "snack", label: "Something small", share: split.rows[1].kcal, roles: ROLES.snack },
    ];
  }

  for (const s of slots) {
    if (s.meal !== "lunch" || kind === "one") continue;
    if (lunchKind === "out") s.budgetOnly = true;
    if (lunchKind === "tiffin") s.tiffin = true;
  }

  // A window moves the slots, never the budget: the first carries breakfast's share and protein.
  const w = restricted ? "none" : a.window;
  if ((w === "16:8" || w === "14:10") && kind !== "one") {
    kind = kind === "two" ? "two" : "window";
    const start = a.windowStart ?? WINDOW_STARTS[w].usual;
    const at = windowTimes(effective, start, WINDOW_STARTS[w].hours);
    const mains = slots.filter((s) => s.meal !== "snack");
    mains.forEach((s, i) => {
      s.time = at[Math.min(i, at.length - 1)];
    });
    if (mains[0]) {
      mains[0].first = true;
      if (kind === "window") mains[0].label = "First meal";
    }
    const snack = slots.find((s) => s.meal === "snack");
    const lunch = slots.find((s) => s.meal === "lunch")?.time;
    const dinner = slots.find((s) => s.meal === "dinner")?.time;
    if (snack && lunch !== undefined && dinner !== undefined) snack.time = (lunch + dinner) / 2;
  } else {
    slots = slots.map(withTime);
  }

  return { kind, slots: sortSlots(slots), unplanned: split.unplanned, fast: false };
}

const LABEL: Record<MealSlot, string> = { breakfast: "Breakfast", lunch: "Lunch", snack: "Snack", dinner: "Dinner" };
const ORDER: Record<MealSlot, number> = { breakfast: 0, lunch: 1, snack: 2, dinner: 3 };
const sortSlots = (s: PlanSlot[]) => [...s].sort((a, b) => ORDER[a.meal] - ORDER[b.meal]);

function fixRounding(slots: PlanSlot[], budget: number): PlanSlot[] {
  const diff = budget - slots.reduce((a, s) => a + s.share, 0);
  if (slots.length && Math.abs(diff) < 50) slots[0].share += round10(diff);
  return slots;
}

/**
 * When the person usually eats each meal, from the log: the median logged time, to the half hour.
 * Used on the days where timing matters — 60+ and low-sugar tablets — so a meal reads "8:30".
 */
export function usualTimes(S: KayaState): Partial<Record<MealSlot, number>> {
  const at: Record<MealSlot, number[]> = { breakfast: [], lunch: [], dinner: [], snack: [] };
  for (const L of Object.values(S.logs)) {
    for (const e of L.food || []) {
      if (L.foodOff && e.id in L.foodOff) continue;
      const d = new Date(e.at);
      at[e.meal]?.push(d.getHours() + d.getMinutes() / 60);
    }
  }
  const out: Partial<Record<MealSlot, number>> = {};
  for (const m of MEALS) {
    const xs = at[m].sort((x, y) => x - y);
    if (xs.length < 3) continue;
    out[m] = Math.round(xs[Math.floor(xs.length / 2)] * 2) / 2;
  }
  return out;
}

/* ---------- what is allowed ---------- */

/** What an own food's name tells of its group — a food the person typed has no tags (ADR-032),
 *  and a planner that cannot place it cannot plan from their log. First match wins, so "paneer
 *  bhurji" is dairy before it is egg. */
const GROUP_WORDS: [FoodGroup, string[]][] = [
  ["sweet", ["barfi", "burfi", "ladoo", "laddu", "halwa", "kheer", "jalebi", "gulab jamun", "mithai", "rasgulla", "peda", "chocolate", "cake"]],
  ["fried", ["samosa", "pakora", "pakoda", "bhajji", "bhaji", "kachori", "chips", "namkeen", "bhujia", "vada"]],
  ["dairy", ["paneer", "curd", "dahi", "raita", "milk", "lassi", "chaas", "buttermilk", "kadhi", "cheese", "yogurt", "yoghurt"]],
  ["egg", ["egg", "anda", "ande", "omelette", "omelet"]],
  ["meat", ["chicken", "mutton", "keema", "lamb", "gosht"]],
  ["fish", ["fish", "prawn", "machli"]],
  ["legume", ["dal", "daal", "chana", "chole", "chhole", "rajma", "soya", "sprout", "moong", "lentil", "chilla", "cheela", "besan", "sambar"]],
  ["grain", ["roti", "chapati", "paratha", "parantha", "rice", "pulao", "poha", "upma", "idli", "dosa", "khichdi", "dalia", "oats", "bread", "samak", "sabudana", "kuttu", "naan", "puri", "poori", "thepla"]],
  ["leafy", ["palak", "saag", "methi", "spinach"]],
  ["veg", ["sabzi", "sabji", "salad", "aloo", "gobi", "vegetable", "bhindi", "lauki", "tinda", "karela", "cucumber"]],
  ["fruit", ["banana", "apple", "orange", "guava", "papaya", "mango", "fruit", "grapes", "pear", "chikoo"]],
  ["nut", ["almond", "badam", "walnut", "peanut", "chia", "seed", "makhana", "cashew", "flax", "nuts"]],
  ["drink", ["chai", "tea", "coffee", "juice", "sharbat", "nimbu", "shikanji"]],
];

export function groupOf(item: Pick<FoodItem, "n" | "gr">): FoodGroup | null {
  if (item.gr) return item.gr;
  for (const [g, words] of GROUP_WORDS) if (words.some((w) => nameHasWord(item.n, w))) return g;
  return null;
}

/** Fast-day words for a food the person typed: the vrat grains and what a vrat plate is made of. */
const VRAT_WORDS = ["vrat", "upvas", "sabudana", "sago", "samak", "sama", "kuttu", "singhara", "rajgira", "makhana", "sendha"];
/** Root vegetables and alliums, the Jain rule for a food without a Jain tag. */
const ROOT_WORDS = ["onion", "garlic", "potato", "aloo", "carrot", "beetroot", "radish", "mooli", "ginger", "arbi", "shakarkandi", "yam"];
const ALLIUM_WORDS = ["onion", "garlic", "pyaaz", "lahsun"];
const NONVEG_FAMILIES = ["chicken", "mutton", "fish", "prawn"];
/** Lactose: milk out, the fermented and set foods still in. */
const LACTOSE_OK = ["curd", "dahi", "raita", "chaas", "buttermilk", "paneer", "cheese", "ghee", "yogurt", "yoghurt"];

function patternOf(S: KayaState): EatingPattern | undefined {
  const p = S.profile.eating?.pattern;
  if (!p) return undefined;
  return p === "satvik" ? "veg" : p;
}

function avoidOf(S: KayaState): { allergens: Allergen[]; lactose: boolean } {
  const avoid = S.profile.eating?.avoid ?? [];
  return { allergens: avoid.filter((a): a is Allergen => a !== "lactose"), lactose: avoid.includes("lactose") };
}

/**
 * Why a food is kept out, in the words a row says after "Out — ", or null when it is allowed.
 * The hard filters: the eating pattern, the never-eat list, a fast day's pool, the Jain root rule.
 *
 * A table row answers from its tags and an unknown is out (ADR-038). A food the person typed has
 * no tags, so it is read by its name: a non-veg or egg word keeps it out of a pattern that has
 * none, and with any allergy on the list it is out — its recipe cannot be read.
 */
export function outReason(S: KayaState, item: FoodItem, opts: { fast?: boolean } = {}): string | null {
  const pattern = patternOf(S);
  const answer = S.profile.eating?.pattern;
  const { allergens, lactose } = avoidOf(S);
  const own = item.src === "c" || item.dt === undefined;
  const words = answer ? PATTERN_WORDS[answer] : "";

  if (own && !item.dt) {
    if (pattern && pattern !== "nonveg") {
      const nonveg = NONVEG_FAMILIES.some((k) => { const f = familyByKey(k); return f ? inFamily(item.n, f) : false; });
      const egg = familyByKey("egg");
      if (nonveg || (pattern !== "egg" && egg && inFamily(item.n, egg))) return `you ${words.replace(/^you /, "")}`;
      if (pattern === "jain" && ROOT_WORDS.some((w) => nameHasWord(item.n, w))) return words;
      if (pattern === "vegan" && groupOf(item) === "dairy") return words;
    }
    if (allergens.length) return "its recipe is not known, and you have foods you never eat";
  } else if (!knownSafe(item, { pattern, avoid: allergens })) {
    if (pattern && knownSafe(item, { pattern }) === false) return words;
    const hit = allergens.find((a) => item.al?.includes(a));
    return hit ? `it has ${hit === "treenut" ? "tree nuts" : hit}, which you never eat` : "its recipe is not known, and you have foods you never eat";
  }
  if (answer === "satvik" && ALLIUM_WORDS.some((w) => nameHasWord(item.n, w))) return words;
  if (lactose && item.al?.includes("milk") && !LACTOSE_OK.some((w) => nameHasWord(item.n, w)))
    return "it has milk, and you keep off lactose";
  if (opts.fast && !fastFood(item)) return "not a fast-day food";
  return null;
}

/** On a fast day: a row tagged `vrat`, or a food of the person's own named as one, or plain dairy,
 *  fruit and nuts, which every vrat plate allows. */
export function fastFood(item: FoodItem): boolean {
  if (item.fl?.includes("vrat")) return true;
  if (item.src !== "c" && item.dt !== undefined) return false;
  if (VRAT_WORDS.some((w) => nameHasWord(item.n, w))) return true;
  const g = groupOf(item);
  return g === "dairy" || g === "fruit" || g === "nut";
}

/* ---------- what the person eats ---------- */

export interface LogStat {
  key: string;
  /** how often it was logged, and in which meal */
  n: number;
  byMeal: Record<MealSlot, number>;
  /** the most recent entry, and the most recent one in each meal — the portion the person uses */
  last: FoodEntry;
  lastByMeal: Partial<Record<MealSlot, FoodEntry>>;
  /** the days it was logged */
  days: string[];
}

export const foodKey = (e: Pick<FoodEntry, "code" | "name">): string => e.code || `name:${e.name.toLowerCase()}`;
const itemKey = (f: Pick<FoodItem, "i" | "n">): string => f.i || `name:${f.n.toLowerCase()}`;

/** The same walk as `intake.ts::frequentFoods`, kept per meal and per day. */
export function logStats(S: KayaState): Map<string, LogStat> {
  const out = new Map<string, LogStat>();
  for (const [ds, L] of Object.entries(S.logs)) {
    for (const e of L.food || []) {
      if (L.foodOff && e.id in L.foodOff) continue;
      const key = foodKey(e);
      let s = out.get(key);
      if (!s) {
        s = { key, n: 0, byMeal: { breakfast: 0, lunch: 0, dinner: 0, snack: 0 }, last: e, lastByMeal: {}, days: [] };
        out.set(key, s);
      }
      s.n++;
      s.byMeal[e.meal]++;
      if (e.at > s.last.at) s.last = e;
      const lm = s.lastByMeal[e.meal];
      if (!lm || e.at > lm.at) s.lastByMeal[e.meal] = e;
      if (!s.days.includes(ds)) s.days.push(ds);
    }
  }
  return out;
}

/** "3 of the last 4 weeks": in how many of the four weeks before `today` a food was logged. */
export function weeksLogged(stat: LogStat | undefined, today: string): number {
  if (!stat) return 0;
  let n = 0;
  for (let w = 0; w < 4; w++) {
    const to = addDays(today, -7 * w);
    const from = addDays(today, -7 * (w + 1));
    if (stat.days.some((d) => d >= from && d < to)) n++;
  }
  return n;
}

/** A candidate food: a table row, a food of the person's own, or — for a logged food whose row
 *  has gone — the entry's own frozen values standing in for it. */
export interface Candidate {
  key: string;
  item: FoodItem;
  group: FoodGroup | null;
  stat?: LogStat;
  /** the entry the item was rebuilt from when no row is left */
  ghost?: FoodEntry;
}

/** A row standing in for a logged food whose row is gone: per 100 g from the entry's own values. */
function ghostItem(e: FoodEntry): FoodItem | null {
  if (!(e.grams > 0) || !(e.qty > 0)) return null;
  const k = (v: number) => (v * 100) / e.grams;
  const oil = e.oilTsp || 0;
  const row: FoodItem = {
    i: e.code || "",
    n: e.name,
    u: e.unit,
    g: e.unit === "g" ? 1 : e.grams / e.qty,
    k: k(Math.max(0, e.kcal - oil * 45)),
    p: k(e.p),
    c: k(e.c),
    f: k(Math.max(0, e.f - oil * 5)),
    src: "c",
  };
  for (const key of ["fb", "sf", "na", "ca", "fe", "pot"] as const) {
    const v = e[key];
    if (typeof v === "number") row[key] = k(v);
  }
  return row;
}

/**
 * A row whose protein, carbohydrate and fat hold more energy than the row itself (4 / 4 / 9) by
 * more than a sixth is a broken row — some of INDB's soups price the stock by the bone — and a
 * planner that trusts it will pick it for its protein. The person's own foods are theirs to judge.
 */
export function plausible(f: Pick<FoodItem, "k" | "p" | "c" | "f">): boolean {
  if (!(f.k > 0)) return false;
  return f.p * 4 + f.c * 4 + f.f * 9 <= f.k * 1.15;
}

/** Every food the planner may consider: what the person logs first, then the table. */
export function candidatesOf(
  S: KayaState,
  foods: readonly FoodItem[],
  lookup: (code: string) => FoodItem | null
): Candidate[] {
  const stats = logStats(S);
  const out: Candidate[] = [];
  const seen = new Set<string>();
  for (const stat of stats.values()) {
    const e = stat.last;
    const row = e.code ? lookup(e.code) : null;
    const item = row ?? ghostItem(e);
    if (!item) continue;
    out.push({ key: stat.key, item, group: groupOf(item), stat, ...(row ? {} : { ghost: e }) });
    seen.add(stat.key);
  }
  for (const f of foods) {
    const key = itemKey(f);
    if (seen.has(key) || !plausible(f)) continue;
    seen.add(key);
    out.push({ key, item: f, group: groupOf(f) });
  }
  return out;
}

/* ---------- portions ---------- */

const HALF_UNITS = ["katori", "bowl", "plate", "glass", "tablespoon", "teaspoon", "dish", "jar", "portion"];

/** Household-unit rounding: grams to 10, a katori, bowl, plate or spoon to the half, everything
 *  counted — a roti, an egg, an idli, a piece — whole. Never below one step. */
export function roundPortion(qty: number, unit: string): number {
  if (unit === "g" || unit === "gm" || unit === "ml") return Math.max(10, Math.round(qty / 10) * 10);
  const u = unit.toLowerCase();
  const step = HALF_UNITS.some((w) => u.includes(w)) ? 0.5 : 1;
  return Math.max(step, Math.round(qty / step) * step);
}

/** The step a portion moves by — what "one more" means in this unit. */
export function portionStep(unit: string): number {
  if (unit === "g" || unit === "gm" || unit === "ml") return 10;
  const u = unit.toLowerCase();
  return HALF_UNITS.some((w) => u.includes(w)) ? 0.5 : 1;
}

/** The portion a person uses of a food in a meal, else one serving. */
export function usualPortion(c: Candidate, meal: MealSlot): { qty: number; unit: string; oilTsp?: number; usual: boolean } {
  const e = c.stat?.lastByMeal[meal] ?? c.stat?.last;
  if (e) return { qty: e.qty, unit: e.unit, ...(e.oilTsp ? { oilTsp: e.oilTsp } : {}), usual: true };
  return { qty: 1, unit: c.item.u, usual: false };
}

/** The entry for `qty` of a candidate: through `entryFor` from its row, or scaled from the logged
 *  entry's own values when the row is gone (invariant 18 either way). */
export function entryOf(
  c: Candidate,
  p: { qty: number; unit: string; oilTsp?: number },
  meal: MealSlot,
  id: string,
  at: number
): FoodEntry | null {
  if (c.ghost) {
    const e = reportion(c.ghost, { qty: p.qty, oilTsp: p.oilTsp ?? 0, id, at });
    return e ? { ...e, meal } : null;
  }
  return entryFor(c.item, { qty: p.qty, unit: p.unit, meal, oilTsp: p.oilTsp, at, id });
}

/* ---------- a seeded tie-break ---------- */

/** A number in [0, 1) from a string: FNV-1a. The same on every phone, so a tie breaks one way. */
export function seeded(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) / 4294967296;
}

/* ---------- filling a day ---------- */

/** Planned or eaten on a day: the log up to today, the plan after it — what a day holds. */
export function dayFood(S: KayaState, ds: string, today: string): FoodEntry[] {
  const plan = planFor(S, ds);
  if (ds > today) return plan;
  const eaten = entriesFor(S, ds);
  const logged = new Set(eaten.map((e) => e.id));
  // A planned item already logged is in the log under `p:<id>`; count it once.
  const open = plan.filter((p) => ![...logged].some((id) => id === `p:${p.id}` || id.startsWith(`p:${p.id}:`)));
  return [...eaten, ...open];
}

export const isGenerated = (id: string): boolean => id.startsWith("g:");

/** The id base of the n-th generated item of a slot (1-based). */
export const genBase = (ds: string, meal: MealSlot, n: number): string => `g:${ds}:${meal}:${n}`;

/** Foods used on a day by key, per meal — for the variety penalties. */
type Usage = Map<string, Map<string, Set<MealSlot>>>;

function recordUse(use: Usage, ds: string, key: string, meal: MealSlot): void {
  let day = use.get(ds);
  if (!day) use.set(ds, (day = new Map()));
  let m = day.get(key);
  if (!m) day.set(key, (m = new Set()));
  m.add(meal);
}

export interface PlanOptions {
  /** the generated table plus the person's own foods (`domain/customFoods.ts::foodTable`) */
  foods: readonly FoodItem[];
  lookup: (code: string) => FoodItem | null;
  /** YYYY-MM-DD */
  today: string;
  /** the day's targets, or null when there is no budget */
  targets: (ds: string, fast: boolean) => PlanTargets | null;
  /** a day the person marked as a fast (package J's mark); a weekday fast in the answers counts too */
  marked?: (ds: string) => boolean;
  /** stamps for the items; the writer keeps them in order */
  now?: number;
  seed?: string;
  /** clock for the age rules */
  clock?: Date;
}

export interface MealPlanOut {
  slot: PlanSlot;
  /** generated items, with base ids `g:<day>:<slot>:<n>` — the writer resolves a taken base */
  items: PlanItem[];
  /** why these, in one line — the meal's footer */
  reason: string;
  /** true when the slot already held food, so nothing was generated */
  filled: boolean;
  /** the first item is the week's small planned treat, kept as it is */
  treat?: boolean;
}

export interface GeneratedDay {
  ds: string;
  skeleton: DaySkeleton;
  meals: MealPlanOut[];
}

interface Ctx {
  S: KayaState;
  opts: PlanOptions;
  cands: Candidate[];
  use: Usage;
  /** per food, how often it was eaten in each meal on the person's own vrat days */
  vrat: Map<string, Record<MealSlot, number>>;
  seed: string;
  clock: Date;
}

/**
 * On a fast day the plan comes from what the person eats on fast days: the logged days on which
 * every food was a fast-day food. Their curd at an ordinary lunch says nothing about a vrat lunch.
 */
function vratCounts(S: KayaState, cands: Candidate[], today: string): Map<string, Record<MealSlot, number>> {
  const byKey = new Map(cands.map((c) => [c.key, c]));
  const out = new Map<string, Record<MealSlot, number>>();
  for (const [ds, L] of Object.entries(S.logs)) {
    if (ds > today) continue;
    const food = (L.food || []).filter((e) => !(L.foodOff && e.id in L.foodOff));
    if (!food.length) continue;
    const all = food.every((e) => {
      const c = byKey.get(foodKey(e));
      return c !== undefined && fastFood(c.item) && outReason(S, c.item, { fast: true }) === null;
    });
    if (!all) continue;
    for (const e of food) {
      const k = foodKey(e);
      const m = out.get(k) ?? { breakfast: 0, lunch: 0, dinner: 0, snack: 0 };
      m[e.meal]++;
      out.set(k, m);
    }
  }
  return out;
}

function makeCtx(S: KayaState, opts: PlanOptions): Ctx {
  const use: Usage = new Map();
  // The last two days before the first planned one, from the log and the plan.
  for (let i = -2; i < 8; i++) {
    const ds = addDays(opts.today, i);
    for (const e of dayFood(S, ds, opts.today)) recordUse(use, ds, foodKey(e), e.meal);
  }
  const cands = candidatesOf(S, opts.foods, opts.lookup);
  return { S, opts, cands, use, vrat: vratCounts(S, cands, opts.today), seed: opts.seed ?? "kaya", clock: opts.clock ?? new Date() };
}

export function isFast(S: KayaState, ds: string, opts: Pick<PlanOptions, "marked">): boolean {
  return answersFast(S, ds) || !!opts.marked?.(ds);
}

const TRIGGER_GROUP: Record<string, FoodGroup> = {
  sweets: "sweet",
  chocolate: "sweet",
  bakery: "sweet",
  fried: "fried",
  chips: "fried",
};

/** The days of the week a small planned treat falls on, counted from the first planned day:
 *  twice a week the first and the fourth (Wed and Sat from a Wednesday), three times every other. */
export function treatDays(mode: string | undefined): number[] {
  switch (mode) {
    case "2x":
      return [0, 3];
    case "3x":
      return [0, 2, 4];
    case "most":
      return [0, 1, 2, 3, 4, 5];
    default:
      return [];
  }
}

/**
 * Plans a week from `opts.today`: every empty slot of the next `span` days, in order, each day
 * seeing what the days before it were given. Nothing is written — the page hands `items` to
 * `store/toggles.ts::writeGenerated`.
 */
export function generateWeek(S: KayaState, opts: PlanOptions, span = 7): GeneratedDay[] {
  const ctx = makeCtx(S, opts);
  const out: GeneratedDay[] = [];
  for (let i = 0; i < span; i++) {
    const ds = addDays(opts.today, i);
    const day = generateDayWith(ctx, ds, { treat: treatDays(S.profile.eating?.triggerMode).includes(i) });
    if (day) out.push(day);
  }
  return out;
}

/** One day's empty slots — or only `only`, for a regenerate. */
export function generateDay(
  S: KayaState,
  ds: string,
  opts: PlanOptions,
  only?: { meal: MealSlot; round: number; avoid: string[] }
): GeneratedDay | null {
  const ctx = makeCtx(S, opts);
  const offset = Math.round((parseDay(ds).getTime() - parseDay(opts.today).getTime()) / 864e5);
  return generateDayWith(ctx, ds, { treat: treatDays(S.profile.eating?.triggerMode).includes(offset), only });
}

function generateDayWith(
  ctx: Ctx,
  ds: string,
  o: { treat: boolean; only?: { meal: MealSlot; round: number; avoid: string[] } }
): GeneratedDay | null {
  const { S, opts } = ctx;
  const fast = isFast(S, ds, opts);
  const T = opts.targets(ds, fast);
  if (!T || planBlocked(S, true)) return null;
  const skeleton = daySkeleton(S, ds, T.kcal, { fast, today: ctx.clock });
  const have = dayFood(S, ds, opts.today);
  const now = opts.now ?? Date.now();

  const meals: MealPlanOut[] = [];
  const dayItems: FoodEntry[] = have.filter((e) => !(o.only && e.meal === o.only.meal && isGenerated(e.id)));
  for (const slot of skeleton.slots) {
    const held = dayItems.filter((e) => e.meal === slot.meal);
    const skip = o.only ? slot.meal !== o.only.meal : false;
    if (held.length || skip || slot.budgetOnly) {
      meals.push({ slot, items: [], reason: mealReason(S, slot, held, skeleton, ctx.clock), filled: held.length > 0 || skip });
      continue;
    }
    const treat = o.treat && slot.meal === (skeleton.slots.some((s) => s.meal === "snack") ? "snack" : "dinner");
    const items = fillSlot(ctx, ds, slot, T, { fast, treat, avoid: o.only?.avoid ?? [], round: o.only?.round ?? 0, now, dayItems });
    for (const e of items) recordUse(ctx.use, ds, foodKey(e), e.meal);
    dayItems.push(...items);
    meals.push({ slot, items, reason: mealReason(S, slot, items, skeleton, ctx.clock), filled: false, ...(treat ? { treat } : {}) });
  }

  // A day already under way is filled, not re-planned: its log is what happened.
  if (ds > opts.today) repairDay(ctx, ds, skeleton, meals, dayItems, T, fast);
  return { ds, skeleton, meals };
}

interface Picked {
  c: Candidate;
  role: Role;
  qty: number;
  unit: string;
  oilTsp?: number;
}

function fillSlot(
  ctx: Ctx,
  ds: string,
  slot: PlanSlot,
  T: PlanTargets,
  o: { fast: boolean; treat: boolean; avoid: string[]; round: number; now: number; dayItems: FoodEntry[] }
): PlanItem[] {
  const picked: Picked[] = [];
  let kcal = 0;
  let protein = 0;
  const floor = slot.meal === "snack" && !slot.first ? 0 : T.mealFloor;
  const share = slot.share;

  const roles: Role[] = [...slot.roles];
  if (o.treat) roles.unshift(role("snack", [TRIGGER_GROUP[(ctx.S.profile.eating?.triggers ?? [])[0] ?? "sweets"] ?? "sweet"]));

  for (const [ri, r] of roles.entries()) {
    const room = share - kcal;
    if (r.optional && room < share * OPTIONAL_ROOM) continue;
    const best = pickFor(ctx, ds, slot, r, {
      ...o,
      inMeal: new Set(picked.map((p) => p.c.key)),
      proteinShort: floor > 0 && protein < floor,
      treatRole: o.treat && ri === 0,
    });
    if (!best) continue;
    let portion = o.treat && ri === 0 ? { qty: 1, unit: best.item.u } : usualPortion(best, slot.meal);
    let e = entryOf(best, portion, slot.meal, "x", 0);
    if (!e) continue;
    // A side that would overflow the meal is left out rather than squeezed in; a role the meal
    // needs is cut to the room left, so one big usual portion does not shrink everything else.
    // The week's small treat is the one planned portion the answers asked for: it stays whole even
    // where the snack's share is smaller, and the heading says so.
    const isTreat = o.treat && ri === 0;
    if (kcal + e.kcal > share * 1.1 && !isTreat) {
      if (r.optional || o.treat) continue;
      const room = Math.max(0, share - kcal);
      const qty = roundPortion((portion.qty * room) / e.kcal, portion.unit);
      const cut = entryOf(best, { ...portion, qty }, slot.meal, "x", 0);
      if (!cut) continue;
      portion = { ...portion, qty };
      e = cut;
    }
    picked.push({ c: best, role: r, ...portion });
    kcal += e.kcal;
    protein += e.p;
    if (o.treat && ri === 0 && kcal >= share * 0.9) break;
  }
  if (!picked.length) return [];

  // Scale the meal to its share, then round each portion to its household unit. The treat keeps
  // its one small piece — a planned portion is the point of it.
  const draft = picked.map((p, i) => entryOf(p.c, p, slot.meal, "x", i)).filter((e): e is FoodEntry => !!e);
  const factor = fitFactor(draft, share) ?? 1;
  const keep = picked.map((p, i) => o.treat && i === 0 && roles[0] === p.role);
  const qtys = picked.map((p, i) => (keep[i] ? p.qty : roundPortion(p.qty * factor, p.unit)));
  settle(picked, qtys, keep, share, slot.meal);
  return picked
    .map((p, i) => entryOf(p.c, { qty: qtys[i], unit: p.unit, oilTsp: p.oilTsp }, slot.meal, genBase(ds, slot.meal, i + 1), o.now + ORDER[slot.meal] * 20 + i))
    .filter((e): e is FoodEntry => !!e);
}

/**
 * Rounding each portion on its own can overshoot a meal by a whole roti or half a katori per item.
 * While the meal is more than 5 % over its share, one household step at a time comes off
 * whichever item brings it closest; a meal that lands under is left under, and said so in its
 * heading ("448 of ~460") — the plan never pads a meal to reach a number.
 */
/** Within this share of the meal's budget, rounding is left as it fell. */
const SETTLE_WITHIN = 0.05;

function settle(picked: Picked[], qtys: number[], keep: boolean[], share: number, meal: MealSlot): void {
  const kcalAt = (i: number, q: number) => entryOf(picked[i].c, { qty: q, unit: picked[i].unit, oilTsp: picked[i].oilTsp }, meal, "x", 0)?.kcal ?? 0;
  let total = qtys.reduce((a, q, i) => a + kcalAt(i, q), 0);
  for (let round = 0; round < 12 && total - share > share * SETTLE_WITHIN; round++) {
    let best: { i: number; q: number; total: number } | null = null;
    for (let i = 0; i < picked.length; i++) {
      if (keep[i]) continue;
      const step = portionStep(picked[i].unit);
      for (const q of [qtys[i] - step, qtys[i] + step]) {
        if (q < step - 1e-9) continue;
        const next = total - kcalAt(i, qtys[i]) + kcalAt(i, q);
        if (Math.abs(next - share) < Math.abs((best?.total ?? total) - share) - 1e-9) best = { i, q, total: next };
      }
    }
    if (!best) return;
    qtys[best.i] = best.q;
    total = best.total;
  }
}

function pickFor(
  ctx: Ctx,
  ds: string,
  slot: PlanSlot,
  r: Role,
  o: { fast: boolean; avoid: string[]; round: number; inMeal: Set<string>; proteinShort: boolean; treatRole: boolean; dayItems: FoodEntry[] }
): Candidate | null {
  const { S } = ctx;
  const dislikes = S.profile.eating?.dislikes ?? [];
  const chewing = S.profile.eating?.chewing;
  let best: Candidate | null = null;
  let bestScore = -Infinity;
  for (const c of ctx.cands) {
    if (!c.group || !r.groups.includes(c.group)) continue;
    if (o.inMeal.has(c.key)) continue;
    if (outReason(S, c.item, { fast: o.fast })) continue;
    // The protein of a meal has to carry some: curd by the half katori is a side, not the protein.
    if (r.key === "protein" && (entryOf(c, usualPortion(c, slot.meal), slot.meal, "x", 0)?.p ?? 0) < PROTEIN_ROLE_MIN_G)
      continue;
    const score = scoreOf(ctx, ds, slot, r, c, { ...o, dislikes, chewing });
    if (score > bestScore) {
      best = c;
      bestScore = score;
    }
  }
  return best;
}

function scoreOf(
  ctx: Ctx,
  ds: string,
  slot: PlanSlot,
  r: Role,
  c: Candidate,
  o: {
    fast: boolean;
    avoid: string[];
    round: number;
    proteinShort: boolean;
    treatRole: boolean;
    dislikes: string[];
    chewing: string | undefined;
  }
): number {
  const st = c.stat;
  // How often the person logs it — in this meal most of all; a food never logged starts at 0. On
  // a fast day, how often on their own fast days, when the log has any.
  const onFast = o.fast && ctx.vrat.size > 0;
  const counts = onFast ? ctx.vrat.get(c.key) : st?.byMeal;
  const total = counts ? counts.breakfast + counts.lunch + counts.dinner + counts.snack : 0;
  let s = counts ? 3 * counts[slot.meal] + 0.5 * (total - counts[slot.meal]) : 0;
  // Staples over dishes when the log has nothing: plain dal and roti before "Beetroot poori".
  if (!st && (c.item.src === "u" || c.item.src === "f")) s += 0.5;
  if (isDisliked({ i: c.item.i, n: c.item.n }, o.dislikes)) s -= 1000;
  if (o.avoid.includes(c.key)) s -= 500;
  // Variety: not twice in a day; not the same meal as the last two days.
  const today = ctx.use.get(ds)?.get(c.key);
  if (today) s -= 12;
  for (const back of [1, 2]) {
    const m = ctx.use.get(addDays(ds, -back))?.get(c.key);
    if (m) s -= m.has(slot.meal) ? (back === 1 ? 6 : 3) : back === 1 ? 2 : 1;
  }
  // Protein while the meal is short of its floor, per 100 kcal of the food.
  // Protein while the meal is short of its floor, per 100 kcal of the food, capped so it tips a
  // choice rather than makes one; a food the person has never logged gets a tenth of it — the
  // plan reaches for what they eat before it reaches for a stranger with a good number.
  if (o.proteinShort && r.key === "protein" && c.item.k > 0)
    s += Math.min(PROTEIN_BONUS_MAX, (c.item.p / c.item.k) * 100) * (st ? 1 : 0.1);
  if (slot.tiffin && (c.item.fl?.includes("tiffin") || (st && st.byMeal.lunch > 0))) s += 1;
  if ((o.chewing === "some" || o.chewing === "yes") && c.item.fl?.includes("soft")) s += 1;
  // The seeded tie-break, and a regenerate's round: the same state gives the same plan.
  return s + seeded(`${ctx.seed}|${ds}|${slot.meal}|${r.key}|${c.key}|${o.round}`) * 0.5;
}

/* ---------- check and repair ---------- */

export interface DayCheck {
  kcal: number;
  protein: number;
  sodium: number;
  /** false when an item lacks sodium — the sum is then a lower bound */
  sodiumKnown: boolean;
  satFat: number;
  /** meals that reached the floor, of the meals planned */
  mealsMet: number;
  mealsCounted: number;
  kcalOk: boolean;
  proteinOk: boolean;
  sodiumOk: boolean;
  satFatOk: boolean;
  totals: IntakeTotals;
}

const MEAL_MET_TOLERANCE_G = 0.5;

export function checkDay(items: FoodEntry[], skeleton: DaySkeleton, T: PlanTargets): DayCheck {
  const t = totalsOf(items);
  const counted = skeleton.slots.filter((s) => !s.budgetOnly && items.some((e) => e.meal === s.meal));
  const mealsMet = counted.filter((s) => {
    const p = items.filter((e) => e.meal === s.meal).reduce((a, e) => a + e.p, 0);
    return p + MEAL_MET_TOLERANCE_G >= T.mealFloor;
  }).length;
  return {
    kcal: t.kcal,
    protein: Math.round(t.p),
    sodium: t.na,
    sodiumKnown: t.known.na >= t.items,
    satFat: t.sf,
    mealsMet,
    mealsCounted: counted.length,
    kcalOk: Math.abs(t.kcal - T.kcal) <= T.kcal * 0.1,
    proteinOk: Math.round(t.p) >= T.protein,
    sodiumOk: t.na <= T.sodiumMax,
    satFatOk: T.satFatMax === null || t.sf <= T.satFatMax,
    totals: t,
  };
}

/**
 * The bounded like-for-like swap loop: for a day short of protein or over a ceiling, one generated
 * item at a time is swapped for a food of the same role and group whose portion is within ±25 % of
 * its kcal, when that improves what failed and breaks nothing else. Six swaps at most; what is
 * still short afterwards is said, not forced.
 */
function repairDay(
  ctx: Ctx,
  ds: string,
  skeleton: DaySkeleton,
  meals: MealPlanOut[],
  dayItems: FoodEntry[],
  T: PlanTargets,
  fast: boolean
): void {
  for (let round = 0; round < 6; round++) {
    const all = [...dayItems];
    const chk = checkDay(all, skeleton, T);
    if (chk.proteinOk && chk.sodiumOk && chk.satFatOk) return;
    let bestGain = 0;
    let bestSwap: { meal: MealPlanOut; idx: number; e: FoodEntry } | null = null;
    const onDay = new Set(all.map(foodKey));
    for (const meal of meals) {
      for (const [idx, old] of meal.items.entries()) {
        if (meal.treat && idx === 0) continue;
        const oldGroup = groupOf({ n: old.name, gr: old.code ? ctx.opts.lookup(old.code)?.gr : undefined });
        if (!oldGroup) continue;
        for (const c of ctx.cands) {
          if (c.group !== oldGroup || onDay.has(c.key)) continue;
          // Like for like means a food the person already eats in this meal — not a dish from the
          // table they have never logged, and not a dinner curry at breakfast.
          if (!c.stat || c.stat.byMeal[meal.slot.meal] === 0) continue;
          if (outReason(ctx.S, c.item, { fast })) continue;
          if (isDisliked({ i: c.item.i, n: c.item.n }, ctx.S.profile.eating?.dislikes ?? [])) continue;
          const portion = usualPortion(c, meal.slot.meal);
          const probe = entryOf(c, portion, meal.slot.meal, old.id, old.at);
          if (!probe || !(probe.kcal > 0)) continue;
          const f = old.kcal / probe.kcal;
          const qty = roundPortion(portion.qty * f, portion.unit);
          const e = entryOf(c, { ...portion, qty }, meal.slot.meal, old.id, old.at);
          if (!e || Math.abs(e.kcal - old.kcal) > old.kcal * 0.25) continue;
          const next = all.map((x) => (x.id === old.id ? e : x));
          const after = checkDay(next, skeleton, T);
          if (!after.kcalOk && chk.kcalOk) continue;
          const gain =
            (chk.proteinOk ? 0 : Math.min(after.protein, T.protein) - Math.min(chk.protein, T.protein)) +
            (chk.sodiumOk ? (after.sodiumOk ? 0 : -1000) : (chk.sodium - after.sodium) / 50) +
            (chk.satFatOk ? (after.satFatOk ? 0 : -1000) : chk.satFat - after.satFat);
          if (gain > bestGain + 1e-9) {
            bestGain = gain;
            bestSwap = { meal, idx, e };
          }
        }
      }
    }
    if (!bestSwap) return;
    const { meal, idx, e } = bestSwap;
    const old = meal.items[idx];
    meal.items[idx] = e;
    const at = dayItems.findIndex((x) => x.id === old.id);
    if (at >= 0) dayItems[at] = e;
  }
}

/* ---------- the words ---------- */

const n = (v: number) => Math.round(v).toLocaleString();

/** "8:30", "1:30", "7:30" — the parent's boards' clock words. */
export function timeWord(h: number): string {
  const hh = Math.floor(h) % 24;
  const mm = Math.round((h - Math.floor(h)) * 60);
  const twelve = hh % 12 === 0 ? 12 : hh % 12;
  return `${twelve}:${String(mm).padStart(2, "0")}`;
}

/**
 * The one-line reason under a meal. It names what shaped the meal, in the order it matters: the
 * medicine's timing, the fast, sugar, chewing; then the two-meal day, the tiffin and the dislikes,
 * the evening's floor, and otherwise where the food came from.
 */
export function mealReason(S: KayaState, slot: PlanSlot, items: FoodEntry[], skeleton: DaySkeleton, clock = new Date()): string {
  const a = S.profile.eating ?? {};
  const meds = a.meds ?? [];
  const words: string[] = [];
  const time = slot.time !== undefined ? timeWord(slot.time) : null;
  // prediabetes too: no sugar in the chai is the same advice at the borderline
  const diabetes = (a.conditions ?? []).some((c) => c === "diabetes" || c === "prediabetes");
  const hasChai = items.some((e) => /chai|tea/i.test(e.name));
  const chew = a.chewing === "some" || a.chewing === "yes";

  if (slot.budgetOnly) return `Canteen lunch: aim for ~${n(slot.share)} kcal and ${PLAN_MEAL_PROTEIN_G} g of protein`;
  if (slot.meal === "breakfast" && meds.includes("thyroid")) words.push("30–60 min after the thyroid tablet");
  else if (slot.meal === "snack" && diabetes && hasChai) words.push("no sugar in the chai");
  else if (slot.meal === "dinner" && chew && older(S, clock)) words.push("soft, easy to chew");
  else if (skeleton.fast) words.push("vrat foods only");
  if (words.length) return time ? `${time} · ${words[0]}` : cap(words[0]);

  if (skeleton.kind === "two" && slot.meal === "breakfast") {
    const p = Math.round(items.reduce((x, e) => x + e.p, 0));
    return `Carries the day: ${p} g protein.`;
  }
  if (slot.light) return "Light, as you answered. Seeds and curd keep protein and fibre up.";
  if (slot.first) return "The first meal carries breakfast’s share and its protein.";
  if (slot.tiffin) {
    const k = (a.dislikes ?? []).length;
    const dis = k ? ` · none of the ${k === 1 ? "food" : `${numberWord(k)} foods`} you dislike` : "";
    return `Tiffin-safe${dis}`;
  }
  if (slot.meal === "dinner" && (a.cravingTime === "evening" || a.cravingTime === "night"))
    return "Protein and fibre kept up in the evening";
  const MORNINGS: Record<MealSlot, string> = { breakfast: "mornings", lunch: "lunches", snack: "snacks", dinner: "dinners" };
  const out = time ? `${time} · ` : "";
  return `${out}${time ? "chosen" : "Chosen"} from what you log most ${MORNINGS[slot.meal]}`;
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const NUMBER_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
export const numberWord = (k: number): string => NUMBER_WORDS[k] ?? String(k);

/** "Thursday" — a day in a sentence about what a swap does to it. */
export function weekdayWord(ds: string): string {
  return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][parseDay(ds).getDay()];
}

/** "Thu" */
export function dowShort(ds: string): string {
  return DOW_LABELS[parseDay(ds).getDay()];
}

const PLURAL_UNITS = ["egg", "piece", "bowl", "tablespoon", "teaspoon", "glass", "cup", "slice", "biscuit", "banana", "orange", "guava", "apple", "plate", "samosa", "dosa", "idli", "pancake", "cookie", "patty", "potato", "tomato", "onion", "carrot", "handful", "bun", "roll"];

/** "2 eggs", "1 katori", "0.5 katori", "10 pieces", "40 g" — how a planned portion reads. */
/** Units the table words longer than a plate needs: "1 tea cup" of chai is "1 cup". */
const UNIT_WORD: Readonly<Record<string, string>> = { "tea cup": "cup" };

export function servingText(qty: number, rawUnit: string): string {
  const unit = UNIT_WORD[rawUnit] ?? rawUnit;
  const q = qty.toLocaleString();
  if (/^\d/.test(unit)) return qty === 1 ? unit : `${q} × ${unit}`;
  if (unit === "g" || unit === "ml") return `${q} ${unit}`;
  if (qty <= 1) return `${q} ${unit}`;
  const words = unit.split(" ");
  const head = words[0];
  const last = words[words.length - 1];
  if (PLURAL_UNITS.includes(last)) {
    const plural = /(s|sh|ch|x)$/.test(last) || last === "potato" || last === "tomato" ? `${last}es` : `${last}s`;
    return `${q} ${[...words.slice(0, -1), plural].join(" ")}`;
  }
  if (PLURAL_UNITS.includes(head) && words.length > 1) {
    return `${q} ${[`${head}s`, ...words.slice(1)].join(" ")}`;
  }
  return `${q} ${unit}`;
}

/** "½ plate", "¾ plate", "1 plate" — the fit panel's portions. */
export function fractionText(qty: number, unit: string): string {
  const whole = Math.floor(qty + 1e-9);
  const rest = qty - whole;
  const frac = Math.abs(rest - 0.25) < 0.01 ? "¼" : Math.abs(rest - 0.5) < 0.01 ? "½" : Math.abs(rest - 0.75) < 0.01 ? "¾" : null;
  if (frac === null) return servingText(qty, unit);
  return `${whole ? whole : ""}${frac} ${unit}`;
}

/* ---------- the check, in words ---------- */

export interface CheckWords {
  /** the first line, 15 ink */
  line: string;
  /** under it, 12.8 muted, "based on the planned items · …" */
  basedOn: string;
  /** a second finding with its own line: the day's kcal when it lands well under */
  second?: { line: string; basedOn: string };
}

/**
 * The planned day's check, as the boards word it. A usual day is one line of figures; a day that
 * lands well under (the two-meal day) or short of protein by more than a tenth says so, how much,
 * and a way to close it; a fast day says there is no deficit and that protein is judged over the
 * week.
 */
export function checkWords(
  items: FoodEntry[],
  skeleton: DaySkeleton,
  T: PlanTargets,
  opts: { lookup?: (code: string) => FoodItem | null; fastName?: string } = {}
): CheckWords {
  const c = checkDay(items, skeleton, T);
  const na = `${c.sodiumKnown ? "" : "≥ "}${n(c.sodium)}`;
  if (skeleton.fast) {
    return {
      line: `No deficit today · protein ${c.protein} of ${T.protein} g — a fast day is judged over the week`,
      basedOn: `based on the planned items · ${n(c.kcal)} of ~${n(T.kcal)} kcal · sodium ~${na} of under ${n(T.sodiumMax)} mg — sendha namak counts`,
    };
  }
  const meals = `${c.mealsMet} of ${c.mealsCounted} meals ≥ ${T.mealFloor} g`;
  const proteinFar = c.protein < T.protein * 0.9;
  const under = T.kcal - c.kcal;
  const farUnder = c.kcal < T.kcal * 0.9;
  const farOver = c.kcal > T.kcal * 1.1;

  if (!proteinFar && !farUnder && !farOver) {
    return {
      line: `Protein ${c.protein} of ${T.protein} g · ${meals} · sodium ${na} mg`,
      basedOn: `based on the planned items · ${n(c.kcal)} of ~${n(T.kcal)} kcal · sodium limit ${n(T.sodiumMax)} mg`,
    };
  }

  let line = `Protein ${c.protein} of ${T.protein} g`;
  if (proteinFar) {
    const light = skeleton.slots.find((s) => s.light);
    line += light ? " — a light evening cannot carry more." : ".";
    const fix = proteinFix(items, skeleton, T, opts.lookup);
    if (fix) line += ` ${fix}`;
    if (light) line += fix ? "; on training days make the evening a full meal." : " On training days make the evening a full meal.";
    else line += fix ? "." : " Nothing more fits under the budget today — a swap for a food with more protein would close it.";
  } else line += ` · ${meals}`;
  const words: CheckWords = { line, basedOn: `based on the planned items · ${meals}` };
  if (farUnder) {
    words.second = {
      line: `${n(c.kcal)} of ~${n(T.kcal)} kcal — ${n(under)} under, which deepens the day’s deficit to ~${n(T.deficit + under)}; fine on a rest day, not on a training day.`,
      basedOn: `based on the planned items · sodium ${na} of ${n(T.sodiumMax)} mg`,
    };
  } else if (farOver) {
    const over = c.kcal - T.kcal;
    const left = T.deficit - over;
    words.second = {
      line: `${n(c.kcal)} of ~${n(T.kcal)} kcal — ${n(over)} over, ${
        left > 0 ? `which takes the day’s deficit down to ~${n(left)}` : "more than the day’s deficit"
      }. Your own meals are never trimmed; a swap or a smaller portion would bring it back.`,
      basedOn: `based on the planned items · sodium ${na} of ${n(T.sodiumMax)} mg`,
    };
  }
  return words;
}

/**
 * A way to close a protein gap without breaking the budget: more of the planned food with the most
 * protein per kcal, as many units as still fit under it and no more than the gap needs.
 * "Two more boiled eggs in the morning (+155 kcal, +13 g) take it to 98 g and still fit".
 */
export function proteinFix(
  items: FoodEntry[],
  skeleton: DaySkeleton,
  T: PlanTargets,
  lookup?: (code: string) => FoodItem | null
): string | null {
  const t = totalsOf(items);
  const gap = T.protein - Math.round(t.p);
  if (gap <= 0) return null;
  let best: { e: FoodEntry; density: number } | null = null;
  for (const e of items) {
    if (!(e.kcal > 0) || !(e.qty > 0)) continue;
    const density = e.p / e.kcal;
    if (!best || density > best.density) best = { e, density };
  }
  if (!best) return null;
  const e = best.e;
  const step = portionStep(e.unit);
  const row = e.code && lookup ? lookup(e.code) : null;
  const one = (k: number): FoodEntry | null =>
    row ? entryFor(row, { qty: step * k, unit: e.unit, meal: e.meal, at: 0, id: "fix" }) : reportion(e, { qty: step * k, oilTsp: 0, id: "fix" });
  let k = 0;
  let add: FoodEntry | null = null;
  for (let i = 1; i <= 12; i++) {
    const x = one(i);
    if (!x || t.kcal + x.kcal > T.kcal) break;
    k = i;
    add = x;
    if (x.p >= gap) break;
  }
  if (!add || !k) return null;
  const slot = skeleton.slots.find((s) => s.meal === e.meal);
  const where = slot ? ` in the ${slot.label.toLowerCase()}` : "";
  // Summed as shown: the line says "+13 g", so 85 and 13 make 98.
  const to = Math.round(t.p) + Math.round(add.p);
  return `${cap(moreOf(k * step, e))}${where} (+${n(add.kcal)} kcal, +${Math.round(add.p)} g) take${k * step === 1 ? "s" : ""} it to ${to} g and still fit`;
}

/** "two more boiled eggs", "one more katori of dal" */
function moreOf(qty: number, e: FoodEntry): string {
  const num = Number.isInteger(qty) ? numberWord(qty) : qty.toLocaleString();
  const m = /^(\w+), (\w+)$/.exec(e.name.trim());
  if (m && m[1].toLowerCase() === e.unit.toLowerCase()) {
    const noun = qty === 1 ? e.unit : servingText(2, e.unit).replace(/^2 /, "");
    return `${num} more ${m[2].toLowerCase()} ${noun}`;
  }
  const unit = qty === 1 ? e.unit : servingText(2, e.unit).replace(/^2 /, "");
  return `${num} more ${unit} of ${shortFoodName(e.name).toLowerCase()}`;
}

/* ---------- the week ---------- */

/** A nutrient the week is judged on, and what closes a gap in it. */
const WEEKLY: { key: "fb" | "fe" | "ca" | "pot"; word: string; unit: string; closer: string; code: string }[] = [
  { key: "fe", word: "iron", unit: "mg", closer: "a katori of chana or palak", code: "U173799" },
  { key: "ca", word: "calcium", unit: "mg", closer: "a katori of curd or a glass of milk", code: "U171284" },
  { key: "fb", word: "fibre", unit: "g", closer: "a katori of dal or sprouts", code: "U175254" },
  { key: "pot", word: "potassium", unit: "mg", closer: "a banana or a katori of dal", code: "U173944" },
];

export interface WeekShort {
  key: "fb" | "fe" | "ca" | "pot";
  avg: number;
  target: number;
  line: string;
}

/**
 * What is still short over the planned days, as weekly averages (plan §3.5.4) — the one furthest
 * behind, when it is under nine tenths of its target, with the food that would close it and how
 * much of the gap a katori a day covers. Null when nothing is that far behind.
 */
export function weekShort(
  days: FoodEntry[][],
  T: Pick<PlanTargets, "fibre" | "iron" | "calcium" | "potassium">,
  lookup?: (code: string) => FoodItem | null
): WeekShort | null {
  const planned = days.filter((d) => d.length);
  if (!planned.length) return null;
  const target = { fe: T.iron, ca: T.calcium, fb: T.fibre, pot: T.potassium };
  let worst: { w: (typeof WEEKLY)[number]; avg: number; target: number; share: number } | null = null;
  for (const w of WEEKLY) {
    const tgt = target[w.key];
    if (!tgt) continue;
    const avg = planned.reduce((a, d) => a + totalsOf(d)[w.key], 0) / planned.length;
    const share = avg / tgt;
    if (share < 0.9 && (!worst || share < worst.share)) worst = { w, avg, target: tgt, share };
  }
  if (!worst) return null;
  const { w, avg, target: tgt } = worst;
  const shown = w.unit === "mg" && w.key !== "fe" ? n(avg) : String(Math.round(avg));
  const gap = tgt - avg;
  const row = lookup?.(w.code);
  const per = row ? ((row[w.key] ?? 0) * row.g) / 100 : 0;
  const how = per >= gap ? "would close it" : per >= gap / 2 ? "would close most of it" : "would help";
  return {
    key: w.key,
    avg,
    target: tgt,
    line: `Still short over the week: ${w.word} ${shown} of ${n(tgt)} ${w.unit} a day — ${w.closer} daily ${how}.`,
  };
}
